<script>
  import { onMount } from "svelte";
  import { formatDate, formatPrice } from "../../services/utilities.js";
  import { user } from "../../models/user.js";
  import {
    pauseSubscription,
    cancelSubscription,
    renewSubscription,
  } from "../../models/orders.js";
  import { subscription } from "../../models/subscription.js";
  import Alert from "../../components/Alert.svelte";
  import Popup from "../../components/Popup.svelte";
  import SubscriptionOptions from "../../components/SubscriptionOptions.svelte";

  let updates = [];
  let period = "";
  let loaded = false;
  let planInCart = null;

  onMount(async () => {
    await loadDetails();
  });

  async function loadDetails() {
    let subData = await user.checkSubscription();
    subscription.setSubscriptionDetails(subData);
    updates = [];
    if (
      $user.subscriptionDetails &&
      Object.keys($user.subscriptionDetails).length > 0
    ) {
      // Determine warnings on account
      if ($user.subscriptionDetails.has_rentals)
        updates.push(
          "You currently have unreturned rentals and may not modify your membership. Please return them first, or reach out to info@puzzleexchange.com with questions",
        );
      else if ($user.subscriptionDetails.num_rentals > 0)
        updates.push(
          "You currently have one unreturned rental and may not cancel or pause your membership. You may renew early if you'd like additional rentals.",
        );

      if (
        !$user.subscriptionDetails.active &&
        $user.subscriptionDetails.end_date === "0001-01-01T00:00:00Z"
      )
        updates.push(
          `Your membership is currently paused. Your subscription will resume on ${formatDate(
            $user.subscriptionDetails.next_bill_date,
            { showTime: false, formatStr: "MMMM dd, yyyy" },
          )}.`,
        );

      // Determine if the users membership period type: monthly or yearly
      if ($user.subscriptionDetails.membership_name.indexOf("Monthly") > -1) {
        period = "Month";
      } else {
        period = "Year";
      }
    }
    planInCart = findPlanInCart();
    loaded = true;
  }

  // A non-member may already have a plan waiting in their cart (e.g. picked
  // before signing up). Read localStorage directly; the cart store isn't
  // loaded on account pages.
  function findPlanInCart() {
    try {
      let items = JSON.parse(localStorage.getItem("cart")) || [];
      let plan = items.find(
        (c) => c.product_type && c.product_type.name === "Subscription",
      );
      if (!plan) return null;
      return {
        title: plan.title.replace(" Plan", ""),
        price: plan.price,
        period: /YEAR/i.test(plan.sku || "") ? "year" : "month",
      };
    } catch (e) {
      return null;
    }
  }

  $: isUnlimited =
    $user?.subscriptionDetails?.membership_name?.indexOf("Denver") > -1;

  /* Params for user actions*/
  let defaultParams = { confirm: false, done: false, error: null };
  let pauseParams = { ...defaultParams };
  let cancelParams = { ...defaultParams };
  let renewParams = { ...defaultParams };
  let changePlanParams = { ...defaultParams, showOptions: false };
  let error = null;
  let selectedAction = null;

  let cancelConfirm = () => {
    resetParams();
  };

  function resetParams() {
    pauseParams = { ...defaultParams };
    cancelParams = { ...defaultParams };
    renewParams = { ...defaultParams };
    changePlanParams = { ...defaultParams, showOptions: false };
    selectedAction = null;
    error = null;
  }

  async function handleUserAction(action) {
    if (action === "pause") {
      let res = await pauseSubscription();
      if (res.error) error = res.error;
      pauseParams.confirm = false;
      pauseParams.done = true;
      selectedAction = "Paused membership";
      loadDetails();
    } else if (action === "cancel") {
      let res = await cancelSubscription();
      if (res.error) error = res.error;
      cancelParams.confirm = false;
      cancelParams.done = true;
      selectedAction = "Cancelled membership";
      user.clearSubscription();
      loadDetails();
    } else if (action === "renew") {
      let res = await renewSubscription();
      if (res.error) {
        error = res.error;
      } else {
        // update details while the payment is going through
        user.setRenewEarlyData();
      }
      renewParams.confirm = false;
      renewParams.done = true;
      selectedAction = "Renewed membership";
      // We can't call loadDetails here because the payment hasn't fully gone through yet
      // loadDetails();
    }
  }

  function generateCancelMessage() {
    let cancelMsg = `<h3>Thinking of Canceling?</h3>`;
    if (isUnlimited) {
      cancelMsg = `${cancelMsg} <p>We're sad to see you go! Canceling now means you'll lose access to unlimited shipments and exclusive member benefits.</p>`;
    } else if ($user.subscriptionDetails.shipments_available > 0) {
      cancelMsg = `${cancelMsg} <p>We're sad to see you go! Canceling now means you'll lose access to your ${$user.subscriptionDetails.shipments_available} shipments and exclusive member benefits.</p>`;
    } else {
      cancelMsg = `${cancelMsg} <p>We're sad to see you go! Canceling now means you'll lose access to your exclusive member benefits.</p>`;
    }
    cancelMsg = `${cancelMsg} <p>You'll also lose access to your Puzzle Stats, including your rank and top brands.</p>`;
    cancelMsg = `${cancelMsg} <p>Want to skip a payment, add a shipment, or take an extended break? Read more on our <a href="/info/member-benefits-">Member Benefits</a> page.</p>`;

    if (
      !isUnlimited &&
      $user.subscriptionDetails.membership_name.indexOf("Casual") == -1
    ) {
      cancelMsg = `${cancelMsg} <p>Want to stay in the puzzle game at a lower cost? You can always switch to a lower plan instead.</p>`;
    }
    cancelMsg = `${cancelMsg} <p><b>Would you still like to cancel your plan?</b></p>`;

    return cancelMsg;
  }
</script>

<h2>Membership</h2>
{#await $user}
  Loading ...
{:then}
  {#if !loaded}
    Loading ...
  {:else if (loaded && !$user.subscriptionDetails) || (Object.keys($user.subscriptionDetails).length === 0 && $user.subscriptionDetails.constructor === Object)}
    {#if planInCart}
      <Alert icon="cart">
        <div class="no-membership">
          <span>
            Your <b>{planInCart.title} - {planInCart.period === "year" ? "Yearly" : "Monthly"}</b>
            plan ({formatPrice(planInCart.price)}/{planInCart.period}) is waiting
            in your cart. Finish checkout to start puzzling.
          </span>
          <a class="btn btn-accent-2" href="/checkout">Finish checkout</a>
        </div>
      </Alert>
    {:else}
      <Alert icon="info">
        <div class="no-membership">
          <span>Your account is ready. Choose a plan to start puzzling.</span>
          <a class="btn btn-accent-2" href="/ways-to-rent">Choose a plan</a>
        </div>
      </Alert>
    {/if}
  {:else}
    <div class="membership-cont">
      <div class="membership-detail">
        <span class="main-text">
          <svg class="icon">
            <use href="/icons.svg#star" />
          </svg>
          {$user.subscriptionDetails.membership_name}
        </span>
        <span class="sub-text">
          Member since {formatDate($user.subscriptionDetails.start_date, {
            formatStr: "MMMM dd, yyyy",
            showTime: false,
          })}
        </span>
      </div>
      <div class="membership-detail">
        <span class="main-text">
          <svg class="icon">
            <use href="/icons.svg#cart" />
          </svg>
          Shipments Available Per {period}
        </span>
        {#if !isUnlimited}
          <span class="sub-text">
            {$user.subscriptionDetails.shipments_per_cycle}
            {$user.subscriptionDetails.shipments_per_cycle > 1
              ? "Orders"
              : "Order"}
          </span>
        {:else}
          <span class="sub-text"> Unlimited </span>
        {/if}
      </div>
      <div class="membership-detail">
        <span class="main-text">
          <svg class="icon">
            <use href="/icons.svg#puzzle-piece-2" />
          </svg>
          Rentals Available Per Shipment
        </span>
        <span class="sub-text">
          {$user.subscriptionDetails.rentals_per_shipment}
          {$user.subscriptionDetails.rentals_per_shipment > 1
            ? "Puzzles"
            : "Puzzle"}
        </span>
      </div>
      <div class="membership-detail">
        <span class="main-text">
          <svg class="icon">
            <use href="/icons.svg#cart" />
          </svg>
          Shipments Remaining
        </span>
        {#if !isUnlimited}
          <span class="sub-text">
            {$user.subscriptionDetails.shipments_available}
            {$user.subscriptionDetails.shipments_available > 1 ||
            $user.subscriptionDetails.shipments_available == 0
              ? "Orders"
              : "Order"}
          </span>
        {:else}
          <span class="sub-text"> Unlimited </span>
        {/if}
      </div>
      <div class="membership-detail">
        <span class="main-text">
          <svg class="icon">
            <use href="/icons.svg#calendar" />
          </svg>
          Next Billing Date
        </span>
        <span class="sub-text">
          You will be automatically charged {formatPrice(
            $user.subscriptionDetails.recurring_price,
          )}
          on {formatDate($user.subscriptionDetails.next_bill_date, {
            showTime: false,
            formatStr: "MMMM dd, yyyy",
          })}
        </span>
      </div>
      {#if updates.length > 0}
        <div class="membership-detail">
          <span class="main-text">
            <svg class="icon">
              <use href="/icons.svg#description" />
            </svg>
            Notes
          </span>
          <span class="sub-text">
            <ul>
              {#each updates as update}
                <li>{update}</li>
              {/each}
            </ul>
          </span>
        </div>
      {/if}
    </div>
    {#if error}
      <Alert type="danger" icon="warning">{error}</Alert>
    {:else if selectedAction}
      <Alert type="success" icon="check">{selectedAction}!</Alert>
    {/if}
    <div>
      <div class="member-btn-group">
        <!-- Has Max rentals, unable to renew early -->
        <!-- Is active and has shipments avail, unable to renew early -->
        <button
          class="btn"
          class:hidden={$user.subscriptionDetails.has_rentals ||
            ($user.subscriptionDetails.shipments_available > 0 &&
              $user.subscriptionDetails.active)}
          on:click|stopPropagation={() => {
            renewParams.confirm = true;
          }}
        >
          Get More Shipments Now
        </button>

        <button
          class="btn"
          on:click|stopPropagation={() => {
            changePlanParams.showOptions = !changePlanParams.showOptions;
          }}
        >
          {changePlanParams.showOptions ? "Hide Plans" : "Change Plan"}
        </button>
      </div>

      <!-- Has any rentals, not able to pause -->
      <div class="member-btn-group">
        <button
          class="btn btn-danger"
          disabled={!$user.subscriptionDetails.active ||
            $user.subscriptionDetails.num_rentals > 0}
          on:click|stopPropagation={() => {
            pauseParams.confirm = true;
          }}
        >
          Pause
        </button>
        <button
          class="btn btn-danger"
          disabled={$user.subscriptionDetails.num_rentals > 0}
          on:click|stopPropagation={() => {
            cancelParams.confirm = true;
          }}
        >
          Cancel
        </button>
      </div>
      <!-- List subscription plan options if they want to change their plan -->

      {#if changePlanParams.showOptions}
        <SubscriptionOptions
          hideLocalPlan={!isUnlimited}
          changingPlan={true}
          currentPlan={$user.subscriptionDetails}
        />
      {/if}
    </div>
  {/if}
{/await}

{#if pauseParams.confirm}
  {#if $user.subscriptionDetails.num_pauses >= 3}
    <Popup
      close={() => {
        cancelConfirm();
      }}
      message="Unable to pause membership, already paused 3 times this year"
    />
  {:else if !$user.subscriptionDetails.bill_monthly}
    <Popup
      close={() => {
        cancelConfirm();
      }}
      message="Unable to pause yearly memberships."
    />
  {:else}
    <Popup
      close={() => {
        cancelConfirm();
      }}
      confirm={() => {
        handleUserAction("pause");
      }}
      confirmText="Yes, pause me"
      cancelText="Go back"
      cancel={() => {
        cancelConfirm();
      }}
      message="<h3>Need a Break?</h3><p>Are you sure you want to pause your membership?</p> <p>Pausing will move your next billing date and you'll lose access to puzzle rentals until your membership resumes next month.</p>
      <p>You have <b>{3 -
        $user.subscriptionDetails.num_pauses} pause(s) remaining</b>.</p>
      <p><b>Would you like to continue?</b></p>
      "
    />
  {/if}
{/if}

{#if cancelParams.confirm}
  <Popup
    close={() => {
      cancelConfirm();
    }}
    confirm={() => {
      handleUserAction("cancel");
    }}
    confirmText="Yes, cancel"
    confirmDanger={true}
    cancelText="See other plans"
    cancel={() => {
      cancelConfirm();
      changePlanParams.showOptions = true;
    }}
    message={generateCancelMessage()}
  />
{/if}

{#if renewParams.confirm}
  <Popup
    close={() => {
      cancelConfirm();
    }}
    confirm={() => {
      handleUserAction("renew");
    }}
    confirmText="Yes, renew now"
    cancel={() => {
      cancelConfirm();
    }}
    cancelText="Go back"
    message="<h3>Ready for More Puzzles?</h3>
    <p>You're about to renew your membership early.</p>
    <ul>
      <li>You will be charged {formatPrice(
      $user.subscriptionDetails.recurring_price,
    )}</li>
      <li>You will receive {$user.subscriptionDetails
      .shipments_per_cycle} shipments immediately</li>
      <li>Your billing cycle will reset to today's date, and you’ll be charged {$user
      .subscriptionDetails.bill_monthly
      ? 'monthly'
      : 'yearly'} going forward</li>

    </ul>
    <p>This may take a few minutes to fully complete.</p>
    <p><b>Would you like to continue?</b></p>
    "
  />
{/if}

<style type="text/less">
  .flex-space-between {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* message + button; the button drops below the text on narrow screens */
  .no-membership {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .no-membership .btn {
    flex-shrink: 0;
  }

  .member-btn-group {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    gap: var(--spacing-xs);
    padding: var(--spacing-xs);
  }

  .member-btn-group button {
    width: 250px !important;
  }

  .membership-detail {
    background: #fafafa;
    border: 1px #00000026 solid;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    margin-bottom: 0.5rem;

    span {
      line-height: 1.5;
    }

    .main-text {
      font-size: 16px;
      letter-spacing: 0.75px;
      font-weight: 600;
    }

    .sub-text {
      font-size: 13px;
    }
  }

  .membership-cont {
    display: flex;
    justify-content: space-evenly;
    flex-direction: column;
    padding: 1rem;
  }

  @media (max-width: 1150px) {
    .membership-detail {
      width: auto;
    }

    .membership-cont {
      margin: auto;
    }
  }

  .membership-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .membership-actions button {
    width: 150px;
  }

  @media (max-width: 500px) {
    .membership-actions button {
      width: auto;
    }

    .member-btn-group {
      display: flex;
      flex-direction: column;
      align-items: center;
      align-self: center;
    }

    .member-btn-group button {
      margin: 0px;
    }
  }
</style>
